import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import BlurredPrompt from "../components/shared/BlurredPrompt";
import { APP_ROUTES } from "../constants/appRoutes";
import { logout } from "../store/authSlice";
import { resetCampaignBuilder } from "../store/campaignBuilderSlice";

const LogoutPromptContext = createContext(null);

export function LogoutPromptProvider({ children }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const requestLogout = useCallback(() => {
        setOpen(true);
    }, []);

    const cancelLogout = useCallback(() => {
        setOpen(false);
    }, []);

    const confirmLogout = useCallback(() => {
        dispatch(logout());
        dispatch(resetCampaignBuilder());
        setOpen(false);
        navigate(APP_ROUTES.HOME, { replace: true });
    }, [dispatch, navigate]);

    const value = useMemo(() => ({ requestLogout }), [requestLogout]);

    return (
        <LogoutPromptContext.Provider value={value}>
            {children}
            <BlurredPrompt
                open={open}
                title="Log out?"
                description="You'll be logged out and redirected to the home"
                confirmLabel="Log out"
                cancelLabel="Stay signed in"
                onConfirm={confirmLogout}
                onCancel={cancelLogout}
                iconClass="bi-box-arrow-right"
                iconWrapperClassName="bg-slate-100 text-slate-700"
            />
        </LogoutPromptContext.Provider>
    );
}

export function useLogoutPrompt() {
    const context = useContext(LogoutPromptContext);

    if (!context) {
        throw new Error("useLogoutPrompt must be used within LogoutPromptProvider");
    }

    return context;
}
